import React, {
  useMemo,
  useEffect,
  useCallback,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { ReportColumn } from './ReportColumnHeader';
import ReportSkeleton from './ReportSkeleton';
import getEmployeeProjectFilters from './EmployeeProjectFilters';
import { getInitialFilters, currencyFormatter, percentFormatter } from '../reportHelpers';
import {
  computeActualTotalSpend,
  processUncategorizedInvoices,
  processUncategorizedUpdates,
} from '../../projects/ProjectProgress/progressHelpers';
import { getIdMap } from '../../helpers/helpers';
import { getProgress } from '../state/reports.actions';
import {
  getProjectScheduleOfValues,
  getScheduleOfValueSections,
} from '../../projects/state/projects.actions';
import { scheduleOfValuesData } from '../../projects/ProjectScheduleOfValues/helpers';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';
import {
  calculateTotalMaterialCostUpdates,
  calculateTotalEquipmentCostUpdates,
} from '../../projects/ProjectBudgetTracking/budgetTrackingHelpers';
import { getInvoices } from '../../payables/invoices/state/invoices.actions';
import useProjectCostcodesFetch from '../../costcodes/useProjectCostcodesFetch';

const getAllSubcontracts = async () => {
  try {
    const {
      data = [],
    } = await axios.get('/projects/subcontracts');
    return data;
  } catch (e) {
    console.error('Error loading subcontracts:', e);
    return [];
  }
};

const getAllCostcodeDetails = async () => {
  try {
    const {
      data = [],
    } = await axios.get('/costcodes/details');
    return data;
  } catch (e) {
    console.error('Error loading costcode details:', e);
    return [];
  }
};

const allColumns = [
  ReportColumn('Project', {
    fixed: 'left',
    dataIndex: 'project',
  }),
  ReportColumn('Cost to Date', {
    key: 'costToDate',
    dataIndex: 'costToDate',
  }),
  ReportColumn('Total Estimated Cost', {
    key: 'totalEstimatedCost',
    dataIndex: 'totalEstimatedCost',
  }),
  ReportColumn('% Completion', {
    key: 'percentCompletion',
    dataIndex: 'percentCompletion',
  }),
  ReportColumn('Current Contract', {
    key: 'currentContract',
    dataIndex: 'currentContract',
  }),
  ReportColumn('Sub-Contract Total Value', {
    key: 'subContractTotalValue',
    dataIndex: 'subContractTotalValue',
  }),
  ReportColumn('Expected Profit', {
    key: 'expectedProfit',
    dataIndex: 'expectedProfit',
  }),
  ReportColumn('Earned Revenue (ER)', {
    key: 'earnedRevenue',
    dataIndex: 'earnedRevenue',
  }),
  ReportColumn('Actual Revenue (AR)', {
    key: 'actualRevenue',
    dataIndex: 'actualRevenue',
  }),
  ReportColumn('Deferred Rev (WIP)', {
    key: 'deferredRev',
    dataIndex: 'deferredRev',
  }),
  ReportColumn('Holdback To Date', {
    key: 'holdbackToDate',
    dataIndex: 'holdbackToDate',
  }),
];

export default function WIPSummary({
  selectAllProjects,
  projects,
  renderRowFilters,
  hasNullProject,
  height,
  settings = {},
  exporter,
  onDataChanged,
  onColumnsChanged,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { phases = [] } = useSelector((state) => state.costcodes);
  const users = useSelector((state) => state.users.users);
  const progresses = useSelector((state) => state.reports.progress);
  const invoices = useSelector((state) => state.invoices.invoices);
  const statuses = useSelector((state) => state.invoices.statuses);
  const vendors = useSelector((state) => state.vendors.vendors);
  const projectCostUpdates = useSelector((state) => state.projects.projectCostUpdates);
  const scheduleOfValues = useSelector((state) => state.projects.scheduleOfValues);
  const scheduleOfValueSections = useSelector((state) => state.projects.scheduleOfValueSections);
  const subContractTotalValues = useSelector((state) => state.projects.newEstimatedSubContractTotalValues);

  const [loading, setLoading] = useState(false);
  const [subcontracts, setSubcontracts] = useState([]);
  const [costcodeDetails, setCostcodeDetails] = useState([]);

  const userMap = useMemo(() => getIdMap(users), [users]);
  const statusMap = useMemo(() => getIdMap(statuses), [statuses]);
  const activeProjects = useMemo(
    () => projects.filter((project) => project.active),
    [projects],
  );

  const [filters, setFilters] = useState(getInitialFilters({
    selectAllProjects,
    allProjects: activeProjects,
    projects: activeProjects,
    hasNullProject,
    settings,
    addUsers: false,
    addPhases: false,
    addCostcodes: false,
  }));

  const loadAllSubcontracts = useCallback(async () => {
    try {
      const allSubcontracts = await getAllSubcontracts();
      setSubcontracts(allSubcontracts);
    } catch (error) {
      console.error('Error loading subcontracts:', error);
    }
  }, []);

  const loadAllCostcodeDetails = useCallback(async () => {
    try {
      const allCostcodeDetails = await getAllCostcodeDetails();
      setCostcodeDetails(allCostcodeDetails);
    } catch (error) {
      console.error('Error loading costcode details:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadAllSubcontracts(),
          loadAllCostcodeDetails(),
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    dispatch(getProgress());
    dispatch(getProjectScheduleOfValues({}));
    dispatch(getScheduleOfValueSections({}));
    dispatch(getInvoices());
  }, []);

  const subcontractsMap = useMemo(() => {
    const map = {};
    subcontracts.forEach((item) => {
      const { projectId } = item;
      if (!map[projectId]) {
        map[projectId] = [];
      }
      map[projectId].push(item);
    });
    return map;
  }, [subcontracts]);

  const rowFilters = useMemo(() => {
    const rfs = getEmployeeProjectFilters({
      onFilterToggle: (type, checked) => {
        if (type === 'projects') {
          setFilters({
            ...filters,
            projects: checked,
          });
        }
      },
      projects: activeProjects,
      showEmployee: false,
      showWorkType: false,
      showBreak: false,
      showProject: true,
      showPhase: false,
      showCostcode: false,
      showServiceLocations: false,
      projectCheckDisabled: true,
      settings,
      t,
      filters,
    });

    return rfs;
  }, [activeProjects, filters, settings, t]);

  const scheduleOfValueSectionMap = useMemo(() => (
    getIdMap(scheduleOfValueSections)
  ), [scheduleOfValueSections]);

  const { uncategorizedMaterialUpdates, uncategorizedEquipmentUpdates } = useMemo(
    () => processUncategorizedUpdates(projectCostUpdates),
    [projectCostUpdates],
  );

  // Filter projects based on selected project filters
  const filteredProjects = useMemo(() => activeProjects.filter(({ name, number }) => {
    const projectLabel = formatProjectLabelFromCompanySettings({
      name,
      number,
      settings,
    });
    return filters.projects.has(projectLabel);
  }), [activeProjects, filters.projects, settings]);

  const filteredProjectIds = useMemo(
    () => filteredProjects.map(({ id }) => id),
    [filteredProjects],
  );

  const {
    projectCostcodes: costcodes,
    loading: costcodesLoading,
  } = useProjectCostcodesFetch({
    projectIds: filteredProjectIds,
    includeCategoryIds: true,
    active: false,
  });

  const isLoading = loading || costcodesLoading;

  // Memoize the data processing to avoid recalculating on every render
  const data = useMemo(
    () => {
      if (isLoading) return [];

      return filteredProjects.map((project) => {
        const {
          id: projectId,
          name,
          estimatedEquipmentCost = 0,
          estimatedMaterialCost = 0,
          estimatedOverheadCost = 0,
          labourCost = 0,
          totalContractAmount = 0,
          estimatedSubContractTotalValue = 0,
          estimatedBurdenRate = 0,
          holdbackPercentage = 0,
        } = project;
        // Column 1: Project Name
        const projectName = name;

        // Column 2: Total Cost-To-Date, seen in projects Progress page
        const projectSubcontracts = subcontractsMap[projectId];
        const uncategorizedInvoices = processUncategorizedInvoices({
          invoices,
          statusMap,
          vendors,
          projectId,
        });

        const costToDate = computeActualTotalSpend({
          phases,
          costcodes,
          projectId,
          progresses,
          projectCostcodeDetailsMap: costcodeDetails,
          userMap,
          filteredSubContracts: projectSubcontracts,
          uncategorizedInvoices,
          uncategorizedMaterialUpdates,
          uncategorizedEquipmentUpdates,
          estimatedBurdenRate,
        });

        // Column 3: Total Estimated Cost, seen in projects Budget page
        const labourBurdenCost = labourCost * estimatedBurdenRate;
        const totalMaterialCostUpdates = calculateTotalMaterialCostUpdates(projectCostUpdates);
        const totalEquipmentCostUpdates = calculateTotalEquipmentCostUpdates(projectCostUpdates);

        const totalCost = (
          estimatedEquipmentCost
          + totalEquipmentCostUpdates
          + estimatedMaterialCost
          + totalMaterialCostUpdates
          + estimatedOverheadCost
          + labourCost
          + labourBurdenCost
          + (subContractTotalValues[projectId] ?? estimatedSubContractTotalValue ?? 0)
        );

        // Column 4: Percent Completion
        const percentCompletion = totalCost === 0 ? 0 : (costToDate / totalCost) * 100;

        const {
          newLiveSummaryValue,
        } = scheduleOfValuesData({
          projectId,
          templateId: null,
          totalBaseContractValue: totalContractAmount,
          holdbackPercentage,
          scheduleOfValues,
          setLogValues: null,
          scheduleOfValueSectionMap,
        });

        const {
          totalBilledToDate,
          holdbackAmount,
          totalContractValue: totalContractAmountWithChanges,
        } = newLiveSummaryValue;

        // Column 5: Contract Total in projects contracts page
        const currentContract = totalContractAmountWithChanges ?? 0;

        // Column 6: Sub-Contract Total Value
        const totalSubContractValue = projectSubcontracts?.reduce((total, subcontract) => (
          total + (subcontract.contractValue ?? 0)
        ), 0);
        const fallbackSubContractTotalValue = subContractTotalValues[projectId]
        ?? estimatedSubContractTotalValue
        ?? 0;

        const subContractTotalValue = projectSubcontracts
          ? totalSubContractValue
          : fallbackSubContractTotalValue;

        // Column 7: Expected Profit
        const expectedProfit = currentContract - totalCost;

        // Column 8: Revenue
        const earnedRevenue = currentContract * (percentCompletion / 100);

        // Column 9: Billings, Total-Billed-To-Date seen in projects Contracts page
        const actualRevenue = totalBilledToDate;

        // Column 10: Deferred Rev (WIP)
        const deferredRev = actualRevenue - earnedRevenue;

        // Column 11: Holdback To Date, seen in projects Contracts page
        const holdbackToDate = holdbackAmount;

        return {
          project: projectName,
          costToDate: currencyFormatter(costToDate),
          totalEstimatedCost: currencyFormatter(totalCost),
          percentCompletion: percentFormatter(percentCompletion),
          currentContract: currencyFormatter(currentContract),
          subContractTotalValue: currencyFormatter(subContractTotalValue),
          expectedProfit: currencyFormatter(expectedProfit),
          earnedRevenue: currencyFormatter(earnedRevenue),
          actualRevenue: currencyFormatter(actualRevenue),
          deferredRev: currencyFormatter(deferredRev),
          holdbackToDate: currencyFormatter(holdbackToDate),
        };
      });
    },
    [
      isLoading,
      filteredProjects,
      subcontractsMap,
      invoices,
      statusMap,
      vendors,
      progresses,
      costcodeDetails,
      userMap,
      uncategorizedMaterialUpdates,
      uncategorizedEquipmentUpdates,
      scheduleOfValues,
      scheduleOfValueSectionMap,
      phases,
      costcodes,
    ],
  );

  useEffect(() => {
    if (!exporter) return undefined;

    exporter.setTopLevelColumns([allColumns.map((column) => column.titleString)]);
    exporter.setTopLevelData(data.map((item) => Object.values(item)));
    exporter.updateSettings({
      hideBodyRow: true,
      hideSummaryRow: true,
      hideDate: true,
    });

    return () => {
      exporter.resetTopLevel();
      exporter.updateSettings({
        hideBodyRow: false,
        hideSummaryRow: false,
        hideDate: false,
      });
    };
  }, [exporter, data]);

  // Update exporter with data and columns for export functionality
  useEffect(() => {
    if (exporter && onDataChanged && onColumnsChanged) {
      onDataChanged(data);
      onColumnsChanged(allColumns);
    }
  }, [data, allColumns, exporter, onDataChanged, onColumnsChanged]);

  return (
    <ReportSkeleton
      loading={isLoading}
      data={data}
      columns={allColumns}
      height={height}
      rowFilters={renderRowFilters(rowFilters)}
    />
  );
}

/* eslint-disable react/forbid-prop-types */
WIPSummary.propTypes = {
  selectAllProjects: PropTypes.bool.isRequired,
  projects: PropTypes.array.isRequired,
  renderRowFilters: PropTypes.func.isRequired,
  hasNullProject: PropTypes.bool.isRequired,
  settings: PropTypes.object,
  height: PropTypes.number,
  exporter: PropTypes.object,
  onDataChanged: PropTypes.func,
  onColumnsChanged: PropTypes.func,
};

WIPSummary.defaultProps = {
  settings: {},
  height: 1000,
  exporter: null,
  onDataChanged: null,
  onColumnsChanged: null,
};
